/**
 * Install-to-home-screen state for the PWA shell.
 *
 * Chromium fires `beforeinstallprompt` once, shortly after load, and never
 * replays it; Safari on iOS has no install API and relies on the Share sheet.
 * The listeners therefore attach when this module loads, which the statically
 * imported app shell does before first paint, and the deferred prompt is held
 * until a user gesture spends it. Components only read the derived affordance.
 */
import { useSyncExternalStore } from "react";

/** Chromium's install event; it is not part of `lib.dom`. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * What the current browser can offer right now:
 * - `prompt`: Chromium holds a deferred install prompt.
 * - `ios-instructions`: Safari on iOS, not installed, hint not dismissed.
 * - `none`: installed, unsupported, or dismissed.
 */
export type PwaInstallAffordance = "prompt" | "ios-instructions" | "none";

export type PwaInstallOutcome = "accepted" | "dismissed" | "unavailable";

const IOS_HINT_DISMISSED_KEY = "xan.pwa-install.ios-hint-dismissed";
const STANDALONE_QUERY = "(display-mode: standalone)";

// Other iOS browsers (Chrome, Firefox, Edge, Opera, DuckDuckGo, Google app)
// run on WebKit and keep the "Safari/" token in their user agent, but they
// cannot install the app. In-app web views drop the token and fail the
// "Safari/" test below.
const NON_SAFARI_IOS_BROWSER = /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|GSA\//;

/**
 * True for Safari on iPhone, iPod, and iPad, the only iOS browser that can add
 * the app to the Home Screen. iPadOS 13+ asks for desktop sites by default and
 * reports a Mac user agent, so a touch screen distinguishes it from macOS.
 */
export function isIosSafari(userAgent: string, maxTouchPoints: number): boolean {
  const isIosDevice = /iPhone|iPad|iPod/.test(userAgent);
  const isDesktopClassIpad = /Macintosh/.test(userAgent) && maxTouchPoints > 1;

  return (
    (isIosDevice || isDesktopClassIpad) &&
    /Safari\//.test(userAgent) &&
    !NON_SAFARI_IOS_BROWSER.test(userAgent)
  );
}

function readIosHintDismissed(): boolean {
  try {
    return localStorage.getItem(IOS_HINT_DISMISSED_KEY) === "1";
  } catch {
    // Storage can be unavailable (blocked cookies, private browsing).
    return false;
  }
}

const listeners = new Set<() => void>();
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisPage = false;
let iosHintDismissed = readIosHintDismissed();

function isInstalled(): boolean {
  // `navigator.standalone` is the iOS-only flag for a Home Screen launch.
  const { standalone } = navigator as Navigator & { standalone?: boolean };

  return window.matchMedia(STANDALONE_QUERY).matches || standalone === true || installedThisPage;
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

window.addEventListener("beforeinstallprompt", (event) => {
  // Suppress the browser's own mini-infobar; the shell menu offers install.
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  notify();
});

window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  installedThisPage = true;
  notify();
});

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  const standaloneQuery = window.matchMedia(STANDALONE_QUERY);
  standaloneQuery.addEventListener("change", onChange);

  return () => {
    listeners.delete(onChange);
    standaloneQuery.removeEventListener("change", onChange);
  };
}

function getAffordance(): PwaInstallAffordance {
  if (isInstalled()) {
    return "none";
  }
  if (deferredPrompt) {
    return "prompt";
  }
  if (!iosHintDismissed && isIosSafari(navigator.userAgent, navigator.maxTouchPoints)) {
    return "ios-instructions";
  }
  return "none";
}

/**
 * Opens the browser's install dialog. Call it from a click handler: browsers
 * reject `prompt()` without a user gesture. The deferred event is single-use,
 * so it is spent immediately; a later `beforeinstallprompt` re-arms it.
 */
async function promptInstall(): Promise<PwaInstallOutcome> {
  const event = deferredPrompt;
  if (!event) {
    return "unavailable";
  }

  deferredPrompt = null;
  notify();
  await event.prompt();
  const { outcome } = await event.userChoice;

  return outcome;
}

function dismissIosHint() {
  iosHintDismissed = true;
  try {
    localStorage.setItem(IOS_HINT_DISMISSED_KEY, "1");
  } catch {
    // The in-memory flag still hides the hint for this page load.
  }
  notify();
}

export function usePwaInstall() {
  const affordance = useSyncExternalStore(subscribe, getAffordance);

  return { affordance, dismissIosHint, promptInstall };
}
